import React from 'react';
import { LegalPageShell, Section, Callout } from '../components/shared/LegalPageShell';

/**
 * PrivacyPage — Stage 20B+ Wave A · /privacy
 *
 * Truthful, calm, short. No generic legal template language.
 * Written in first-person-plural ("we") as a real company would.
 */
const PrivacyPage = () => (
  <LegalPageShell
    eyebrow="Privacy"
    title="Your data, quietly protected."
    subtitle="Kindra is a personal health companion. That means we take custody of sensitive context — your reports, your conditions, the notes you add. This page is what we do with it, in plain English."
    updated="30 April 2026"
    current="/privacy"
  >
    <Section eyebrow="The short version" title="In one paragraph.">
      <p>
        We store your health information only to help you understand it — never to sell it, never to train external models, never to share it with marketers or insurers. You can export everything you've added, and you can delete your account at any time.
      </p>
    </Section>

    <Section title="What we collect">
      <p>
        When you sign up: your email, name, and a password. When you use Kindra: the reports you upload, the conditions and medications you add, the symptoms you log, the notes you write to yourself, and the chat conversations you have with Kindra. We also collect basic usage signals (what pages you open, what features you use) so we can improve the product.
      </p>
    </Section>

    <Section title="Who sees it">
      <ul className="space-y-2 list-disc pl-5">
        <li>You, when you sign in.</li>
        <li>The AI model that answers your questions, each time you ask one. It receives only the context needed for that answer and does not retain anything after the response.</li>
        <li>Our payment processor (Stripe), when you subscribe — we never see or store your card details.</li>
        <li>Our email provider (Brevo), when we send you account or weekly briefing emails.</li>
      </ul>
      <p>
        That's the full list. We don't sell your data, we don't share it with advertisers, and we don't use it to train models that anyone else benefits from.
      </p>
    </Section>

    <Callout testid="privacy-never-callout">
      <p className="font-medium">We will never:</p>
      <ul className="mt-3 space-y-1.5 list-disc pl-5">
        <li>Sell your health information to a third party.</li>
        <li>Hand your data to an insurer, employer, or data broker.</li>
        <li>Use your reports or chats to train models that serve anyone but you.</li>
      </ul>
    </Callout>

    <Section title="Your controls">
      <p>
        You own your data. From inside the app you can edit or delete any condition, medication, report, or note. The "Export my data" option in Settings returns a single file with everything on your account. Deleting your account removes your records on our side; anything we hold in backups is purged within 30 days.
      </p>
    </Section>

    <Section title="How long we keep it">
      <p>
        As long as you have an account with us. If you delete your account, your records are removed within 30 days. We never retain deleted records longer than that period.
      </p>
    </Section>

    <Section title="Children and minors">
      <p>
        Kindra is intended for adults (18+). If you're under 18, please don't create an account without a parent or guardian involved.
      </p>
    </Section>

    <Section title="Changes to this page">
      <p>
        If we materially change how we handle your data, we'll notify every active user by email before the change takes effect — not by silently updating a page.
      </p>
    </Section>

    <Section title="Questions">
      <p>
        Email us at <a href="mailto:hello@kindraai.dev" className="underline underline-offset-2 hover:text-[#0E1C2E]" style={{ color: '#0E1C2E' }}>hello@kindraai.dev</a>. A real person reads and responds.
      </p>
    </Section>
  </LegalPageShell>
);

export default PrivacyPage;
